import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './AxiosBaseQuery';
import { V3_APP_BASE_URL } from '@/constants/Config';
import type { IAccountMember } from '@/common/interfaces/user.interface';

interface IErrRes {
  status: string | number;
  data: { message: string };
}

interface IRemoveClient {
  status: string | number;
  message: string;
}

/**  Endpoint to fetch and post account related work.  */
export const accountsQuery = createApi({
  reducerPath: 'accountsQuery',
  tagTypes: ['Account'],
  baseQuery: axiosBaseQuery({
    baseUrl: V3_APP_BASE_URL,
  }),
  endpoints: (builder) => ({
    getAccountClients: builder.query({
      query: (accountId) => ({
        url: `api/cp/account/client/${accountId}`,
        method: 'get',
      }),
      providesTags: ['Account'],
      transformResponse: (response: { clients: IAccountMember }) => response.clients,
      transformErrorResponse: (response: { error: IErrRes }) => response,
    }),
    removeAccountClient: builder.mutation({
      query: ({ data }) => ({
        url: `api/v4/account/contact`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Account'],
      transformResponse: (response: IRemoveClient) => response,
      transformErrorResponse: (response: { error: IErrRes }) => response,
    }),
    addAccountMembers: builder.mutation({
      query: ({ data }) => ({
        url: `api/cp/account/add-member`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: { invites: string[] }) => ({
        message: 'Invite sent.',
        status: 'success',
        invites: response.invites,
      }),
      transformErrorResponse: () => ({
        message: 'Invite failed.',
        status: 'error',
        invites: [],
      }),
    }),
    getAccountInvites: builder.query({
      query: () => ({
        url: `api/cp/account/invite`,
        method: 'GET',
      }),
    }),
    sendAccountRequestMails: builder.mutation({
      query: () => ({
        url: 'api/cp/account/join-request',
        method: 'post',
      }),
    }),
    getPendingMemberApprovals: builder.query({
      query: () => ({
        url: 'api/cp/account/pending-requests',
        method: 'GET',
      }),
    }),
    updatePendingMemberApprovals: builder.mutation({
      query: ({ data }) => ({
        url: 'api/cp/account/update-request',
        method: 'post',
        data,
      }),
      invalidatesTags: ['Account'],
    }),
  }),
});

export const {
  useGetAccountClientsQuery,
  useRemoveAccountClientMutation,
  useLazyGetAccountClientsQuery,
  useAddAccountMembersMutation,
  useLazyGetAccountInvitesQuery,
  useSendAccountRequestMailsMutation,
  useLazyGetPendingMemberApprovalsQuery,
  useUpdatePendingMemberApprovalsMutation,
} = accountsQuery;
